/**
 * PostHog in the browser: pageviews, autocaptured taps, heatmaps, dead and
 * rage clicks, web vitals, JavaScript errors, session replay, and the UI
 * events below. State changes (an entry saved, a baby added) are recorded by
 * the server instead (server/analytics.ts), for taps and AI assistants alike;
 * the tracing headers configured here tie them to this session's replay.
 *
 * Off unless the build had PUBLIC_POSTHOG_KEY. Requests go to the same-origin
 * `/ingest` proxy (server/ingest.ts). A parent can switch it off for this
 * browser in Settings. What reaches PostHog is limited by analytics-privacy.ts:
 * no care-log words in replays or autocaptured elements, no OAuth values in URLs.
 */
import posthog, { type CaptureResult } from "posthog-js";
import {
  maskReplayAttribute,
  maskReplayText,
  redactUrl,
  redactUrlProperties,
  SENSITIVE_PARAMS,
} from "./analytics-privacy.ts";
import { CLIENT_VERSION } from "./update.ts";

/**
 * A `PUBLIC_*` build variable. Bun inlines a set one as a string literal; an
 * unset one stays `process.env.…`, which throws in a browser (no `process`).
 */
function publicEnv(read: () => string | undefined): string {
  try {
    return read() ?? "";
  } catch {
    return "";
  }
}

const KEY = publicEnv(() => process.env.PUBLIC_POSTHOG_KEY);
const HOST = publicEnv(() => process.env.PUBLIC_POSTHOG_HOST) || "https://us.i.posthog.com";

/** The PostHog app for an ingestion host (`us.i.posthog.com` → `us.posthog.com`), for toolbar links. */
function uiHost(host: string): string {
  return host
    .replace(/\/+$/, "")
    .replace(/^(https:\/\/[a-z]+)\.i\.posthog\.com$/, "$1.posthog.com");
}

export const analyticsEnabled = KEY !== "" && typeof window !== "undefined";

/** Every client-side event, so a typo is a type error rather than a new event. */
export type ClientEvent =
  | "sign in started"
  | "sign in failed"
  | "signed out"
  | "log sheet opened"
  | "assistant change opened"
  | "baby tab switched"
  | "earlier entries shown"
  | "night mode toggled"
  | "entry removal started"
  | "entry removal undone"
  | "entry edit opened"
  | "entry edit undone"
  | "api write failed"
  | "api read failed"
  | "needs a look tapped"
  | "chart day selected"
  | "chart tab selected"
  | "chart zoom selected"
  | "note word tapped"
  | "report print opened"
  | "mcp url copied"
  | "assistant prompt copied"
  | "assistant add link opened"
  | "assistant tip answered"
  | "welcome answered"
  | "home screen step shown"
  | "home screen step dismissed"
  | "home screen install tapped"
  | "invite code copied"
  | "assistant consent answered"
  | "feedback opened"
  | "push permission answered"
  | "push opened"
  | "app updated";

/** Installed to the home screen, or in a browser tab. */
export function displayMode(): "standalone" | "browser" {
  const ios = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios || window.matchMedia?.("(display-mode: standalone)").matches
    ? "standalone"
    : "browser";
}

/** Call once, before React renders. */
export function initAnalytics(): void {
  if (!analyticsEnabled) return;
  posthog.init(KEY, {
    api_host: "/ingest",
    ui_host: uiHost(HOST),
    defaults: "2026-08-30",
    // Anonymous visitors (the landing page) don't become person profiles.
    person_profiles: "identified_only",
    capture_pageview: "history_change",
    capture_pageleave: true,
    capture_dead_clicks: true,
    enable_heatmaps: true,
    capture_performance: { web_vitals: true, network_timing: true },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      // Console text can carry whatever a library logged; errors are enough.
      capture_console_errors: false,
    },
    // Autocapture keeps which element was tapped, never its words or attribute
    // values: tabs, sheet titles, and aria-labels name babies and medicines.
    mask_all_text: true,
    mask_all_element_attributes: true,
    // OAuth values in the address bar (/login, /consent) never leave the page.
    mask_personal_data_properties: true,
    custom_personal_data_properties: [...SENSITIVE_PARAMS],
    before_send: redactEvent,
    // Same-origin API calls carry this session's id, so the server's events
    // (entry created, …) land in the same session and replay.
    tracing_headers: [window.location.hostname],
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      maskTextFn: maskReplayText,
      maskAttributeFn: maskReplayAttribute,
      // Explicitly off, so a project setting can't turn them on: request and
      // response bodies are the household's log.
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: (request) => ({ ...request, name: redactUrl(request.name) }),
    },
    // Console output sits outside the replay masks (an error message can
    // name a baby), so it is never recorded, whatever the project setting.
    enable_recording_console_log: false,
    loaded: () => {
      setSuperProperty("display_mode", displayMode());
      // Which build sent it, so an error from an old client reads as one.
      if (CLIENT_VERSION) setSuperProperty("app_version", CLIENT_VERSION);
    },
  });
}

/** A last pass over every event: no sensitive query values in any URL-like property. */
function redactEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  event.properties = redactUrlProperties(event.properties);
  if (event.$set) event.$set = redactUrlProperties(event.$set);
  if (event.$set_once) event.$set_once = redactUrlProperties(event.$set_once);
  return event;
}

/** Record a UI event. Never send names, notes, amounts, or anything else from the log. */
export function track(event: ClientEvent, properties?: Record<string, unknown>): void {
  if (!analyticsEnabled) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics must never break a tap.
  }
}

/** Send an error React caught (an error boundary) or couldn't (a crashed render). */
export function captureError(error: unknown, properties?: Record<string, unknown>): void {
  if (!analyticsEnabled) return;
  try {
    posthog.captureException(error, properties);
  } catch {
    // ignore
  }
}

let household: string | null = null;
let householdTraits: { babies: number } | null = null;

/**
 * The signed-in parent and their household. Name and email go to the person
 * profile so support can find an account; the household is a PostHog group.
 */
export function identify(
  user: { id: string; email: string; name: string },
  organizationId: string | null,
): void {
  if (!analyticsEnabled) return;
  if (posthog.get_distinct_id() !== user.id) {
    posthog.identify(user.id, { email: user.email, name: user.name });
  }
  if (organizationId && organizationId !== household) {
    household = organizationId;
    posthog.group("household", organizationId, householdTraits ?? undefined);
  }
}

/**
 * Household facts worth slicing by (how many babies), as group properties.
 * Kept until `identify` names the household if they arrive first (React runs
 * the app shell's effects before the session gate's).
 */
export function describeHousehold(traits: { babies: number }): void {
  if (!analyticsEnabled) return;
  householdTraits = traits;
  posthog.register({ babies: traits.babies });
  if (household) posthog.group("household", household, traits);
}

/** This device's own super properties (display mode, night mode), which outlive a sign-out. */
const deviceProperties: Record<string, string | number | boolean> = {};

/**
 * Forget the parent on sign-out, so the next person on this phone starts
 * fresh. `reset` clears every super property, so the device's are put back.
 */
export function resetAnalytics(): void {
  household = null;
  householdTraits = null;
  if (!analyticsEnabled) return;
  posthog.reset();
  posthog.register(deviceProperties);
}

/** The session ended without a sign-out (expired or revoked): forget whoever was identified. */
export function forgetSignedOutUser(): void {
  if (analyticsEnabled && posthog.get_property("$user_state") === "identified") resetAnalytics();
}

/** A property of this device that every following event carries (e.g. night mode). */
export function setSuperProperty(name: string, value: string | number | boolean): void {
  deviceProperties[name] = value;
  if (analyticsEnabled) posthog.register({ [name]: value });
}

/** Whether this browser shares usage analytics and replays (Settings). */
export function analyticsAllowed(): boolean {
  return analyticsEnabled && !posthog.has_opted_out_capturing();
}

/** Sent on API calls from a browser that opted out; the server drops its events for them. */
export const NO_ANALYTICS_HEADER = "x-babia-no-analytics";

/** This browser turned usage analytics off in Settings. */
export function analyticsOptedOut(): boolean {
  return analyticsEnabled && posthog.has_opted_out_capturing();
}

export function setAnalyticsAllowed(allowed: boolean): void {
  if (!analyticsEnabled) return;
  if (allowed) posthog.opt_in_capturing();
  else posthog.opt_out_capturing();
}

export { posthog };
